import {
    Component,
    OnInit,
    OnDestroy,
    inject,
    Input,
    ElementRef,
    HostBinding,
    NgZone,
    ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { DynamicFormComponent } from '../shared/dynamic-form/dynamic-form.component';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { PolicyState } from '../store/policy/policy.reducer';
import { Store } from '@ngrx/store';
import { getAuthToken } from '../auth.interceptor';
import { loadPolicyTermDetail, loadPolicyData } from '../store/policy/policy.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { AnimationComponent } from '../shared/animation/animation.component';

type TermEntry = { term: string; startDate: string; endDate: string };

@Component({
    selector: 'app-policy-number',
    imports: [DynamicFormComponent, AnimationComponent],
    templateUrl: './policy-number.component.html',
    styleUrl: './policy-number.component.scss',
})
export class PolicyNumberComponent implements OnInit, OnDestroy {
    @Input() isLoading = false;
    @HostBinding('class.scrolled') isScrolled = false;
    @HostBinding('attr.id') hostId = 'policy-number';

    private store = inject(Store<{ policy: PolicyState }>);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private el = inject(ElementRef);
    private ngZone = inject(NgZone);
    private formSubscription?: Subscription;
    private storeSubscription?: Subscription;
    private scrollListener?: () => void;

    private cdr = inject(ChangeDetectorRef);
    private isUserSelection = false;

    policyForm = new UntypedFormArray([]);
    policyTermField: any = {
        name: 'policyTerm',
        id: 'policyTerm',
        caption: 'Policy Term',
        type: 'dropdown',
        value: '',
        placeholder: 'Select...',
        required: false,
        helpIcon: false,
        disabled: false,
        options: { optionsList: [] },
    };
    policyNumber: string = '';
    isThirdParty: boolean = false;
    sessionId: string | null = null;
    private termMap = new Map<string, TermEntry>();
    private lastBuiltPolicyNumber: string = '';
    private isUserChanging = false;
    private lastRenderedTerm: string = '';

    policyTermFields: any[] = [];

    getPolicyTermFields() {
        return [this.policyTermField];
    }

    ngOnInit() {
        this.isThirdParty = this.router.url.includes('third-party');
        this.sessionId = this.route.snapshot.queryParamMap.get('sessionId');
        this.initScrollListener();

        this.storeSubscription = combineLatest([
            this.store.select((state) => state.policy.policyNumber),
            this.store.select((state) => state.policy.termData),
            this.store.select((state) => state.policy.policyTerm),
        ]).subscribe(([policyNumber, termData, policyTerm]) => {
            this.policyNumber = policyNumber ?? '';

            if (!termData.length) {
                // Terms have been cleared — a new policy load is in progress.
                // Reset so the dropdown rebuilds fresh when the new terms arrive.
                this.lastBuiltPolicyNumber = '';
                return;
            }

            this.termMap = new Map(termData.map((t) => [t.term, t]));
            const optionsList = termData.map((t) => ({ caption: String(t.term), value: String(t.term) }));
            const resolvedTerm = String(
                termData.find((t) => Number(t.term) === Number(policyTerm))?.term ?? termData[0].term
            );

            const isPolicyChanged = this.lastBuiltPolicyNumber !== policyNumber;

            if (isPolicyChanged) {
                this.lastBuiltPolicyNumber = policyNumber ?? '';
                this.policyForm = new UntypedFormArray([]);
                this.policyTermField = {
                    name: 'policyTerm',
                    id: 'policyTerm',
                    caption: 'Policy Term',
                    type: 'dropdown',
                    value: resolvedTerm,
                    placeholder: 'Select...',
                    required: false,
                    helpIcon: false,
                    disabled: false,
                    options: { optionsList },
                };
            } else {
                this.cdr.detectChanges();
                if (!this.isUserSelection) {
                    if (this.policyTermField.value !== resolvedTerm) {
                        this.policyTermField = {
                            ...this.policyTermField,
                            value: resolvedTerm,
                            options: { optionsList },
                        };
                        this.cdr.detectChanges();
                        const control = this.policyForm.controls[0]?.get('policyTerm');
                        if (control && String(control.value) !== resolvedTerm) {
                            this.isUserChanging = true;
                            control.setValue(resolvedTerm, { emitEvent: false });
                            this.cdr.detectChanges();
                            this.isUserChanging = false;
                        }
                    }

                    if (this.lastRenderedTerm !== resolvedTerm) {
                        this.lastRenderedTerm = resolvedTerm;
                        this.policyTermFields = [{ ...this.policyTermField }];
                        this.cdr.detectChanges();
                    }
                }
                this.isUserSelection = false;
            }

            if (isPolicyChanged) {
                this.lastRenderedTerm = resolvedTerm;
                this.policyTermFields = [this.policyTermField];
                this.cdr.detectChanges();
            } else if (this.lastRenderedTerm !== resolvedTerm) {
                this.lastRenderedTerm = resolvedTerm;
                this.policyTermFields = [{ ...this.policyTermField }];
                this.cdr.detectChanges();
            }
        });
    }

    onFormLoaded(form: UntypedFormGroup) {
        this.formSubscription?.unsubscribe();
        const control = form.get('policyTerm');

        if (control) {
            if (this.policyTermField.value && control.value !== this.policyTermField.value) {
                this.isUserChanging = true;
                control.setValue(this.policyTermField.value, { emitEvent: false });
                this.isUserChanging = false;
            }
            this.formSubscription = control.valueChanges.pipe(distinctUntilChanged()).subscribe((selectedTerm) => {
                if (this.isUserChanging || !selectedTerm) return;
                this.callLoadDCPolicyAndBillingAPI(selectedTerm);
            });
        }
    }

    callLoadDCPolicyAndBillingAPI(policyTerm: string) {
        this.lastRenderedTerm = policyTerm;
        this.isUserSelection = true;
        this.policyTermField = { ...this.policyTermField, value: policyTerm };
        const effectiveDate = this.termMap.get(policyTerm)?.startDate;
        const expireDate = this.termMap.get(policyTerm)?.endDate;
        this.store.dispatch(loadPolicyData({ policyNumber: this.policyNumber, policyTerm, effectiveDate, expireDate }));
    }

    callPolicyOrchestrationAPI(termLabel: string) {
        const effectiveDate = this.termMap.get(termLabel)?.startDate ?? '';
        if (this.isThirdParty && this.sessionId) {
            this.store.dispatch(
                loadPolicyTermDetail({
                    payload: {
                        pathParameters: {
                            policyNumber: this.policyNumber,
                            requestType: 'getPolicySnapshotForTP',
                            sessionId: this.sessionId,
                            termNo: termLabel,
                            effectiveDate,
                        },
                    },
                    policyTerm: termLabel,
                })
            );
        } else {
            this.store.dispatch(
                loadPolicyTermDetail({
                    payload: {
                        pathParameters: {
                            authorizationToken: `Bearer ${getAuthToken()}`,
                            policyNumber: this.policyNumber,
                            requestType: 'getPolicySnapshotForCSR',
                            termNo: termLabel,
                            effectiveDate,
                        },
                        documentsPayload: {
                            sorts: [],
                            index: 0,
                            limit: 1000,
                            Category: [],
                            'Communication Sent': {},
                            'Generated / Received date': {},
                            Policy_Term: termLabel,
                        },
                    },
                    policyTerm: termLabel,
                })
            );
        }
    }

    ngOnDestroy() {
        this.formSubscription?.unsubscribe();
        this.storeSubscription?.unsubscribe();
        if (this.scrollListener) {
            const container = this.el.nativeElement.closest('.profile-activities-container');
            container?.removeEventListener('scroll', this.scrollListener);
        }
    }

    private initScrollListener() {
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                const container = this.el.nativeElement.closest('.profile-activities-container');
                if (container) {
                    this.scrollListener = () => {
                        const scrolled = container.scrollTop > 0;
                        if (scrolled !== this.isScrolled) {
                            this.ngZone.run(() => (this.isScrolled = scrolled));
                        }
                    };
                    container.addEventListener('scroll', this.scrollListener);
                }
            });
        });
    }
}
